import React, { useState } from "react";
import { Send, CheckCircle2, Clock } from "lucide-react";
import { widgetAPI } from "../utils/api";
import { logger } from "../utils/logger";

const OfflineForm = ({ config, isDark, primaryColor, backgroundStyle, nextAvailableAt, heading, description, onContinueChat, inline }) => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await widgetAPI.submitOfflineMessage(formData.name, formData.email, formData.message);
      setSubmitted(true);
      logger.log("[OfflineForm] Message submitted successfully");
    } catch (err) {
      logger.error("[OfflineForm] Submit error:", err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatNextAvailable = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return new Intl.DateTimeFormat(undefined, {
        weekday: "long",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(date);
    } catch {
      return null;
    }
  };

  const nextTime = formatNextAvailable(nextAvailableAt);

  if (submitted) {
    return (
      <div className={`${inline ? '' : 'flex-1'} flex items-center justify-center p-6`}>
        <div className="text-center max-w-sm">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <CheckCircle2 size={32} style={{ color: primaryColor }} />
          </div>
          <h3 className={`text-lg font-bold mb-2 ${isDark ? "text-gray-100" : "text-gray-900"}`}>
            Message Sent!
          </h3>
          <p className={`text-sm leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Thank you for reaching out. We'll get back to you at <strong>{formData.email}</strong> as soon as possible.
          </p>
          {nextTime && (
            <div className={`mt-4 flex items-center justify-center gap-2 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              <Clock size={14} />
              <span>We'll be back {nextTime}</span>
            </div>
          )}
          {onContinueChat && (
            <button
              type="button"
              onClick={onContinueChat}
              className={`mt-4 text-sm font-medium underline transition-all ${isDark ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
            >
              Continue chatting with AI
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${inline ? '' : 'flex-1 overflow-y-auto'} p-5`}>
      <div className="max-w-sm mx-auto">
        {/* Offline status icon */}
        <div className="text-center mb-5">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <Clock size={28} style={{ color: primaryColor }} />
          </div>
          <h3 className={`text-lg font-bold mb-1 ${isDark ? "text-gray-100" : "text-gray-900"}`}>
            {heading || "We're currently offline"}
          </h3>
          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {description || "Leave us a message and we'll get back to you!"}
          </p>
          {nextTime && (
            <div className={`mt-2 flex items-center justify-center gap-1.5 text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              <Clock size={12} />
              <span>Back {nextTime}</span>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <input
              type="text"
              placeholder="Your name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              required
              maxLength={100}
              className={`w-full border-2 ${isDark ? "border-gray-600 bg-gray-700 text-gray-100 placeholder:text-gray-400" : "border-gray-200 bg-white text-gray-900 placeholder:text-gray-400"} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
              style={{ "--tw-ring-color": primaryColor }}
            />
          </div>
          <div>
            <input
              type="email"
              placeholder="Your email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              required
              maxLength={255}
              className={`w-full border-2 ${isDark ? "border-gray-600 bg-gray-700 text-gray-100 placeholder:text-gray-400" : "border-gray-200 bg-white text-gray-900 placeholder:text-gray-400"} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all`}
              style={{ "--tw-ring-color": primaryColor }}
            />
          </div>
          <div>
            <textarea
              placeholder="Your message"
              value={formData.message}
              onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
              required
              rows={4}
              maxLength={2000}
              className={`w-full border-2 ${isDark ? "border-gray-600 bg-gray-700 text-gray-100 placeholder:text-gray-400" : "border-gray-200 bg-white text-gray-900 placeholder:text-gray-400"} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all resize-none`}
              style={{ "--tw-ring-color": primaryColor }}
            />
          </div>

          {error && (
            <p className="text-red-500 text-xs text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !formData.name.trim() || !formData.email.trim() || !formData.message.trim()}
            className="w-full text-white py-3 rounded-xl transition-all font-medium text-sm shadow-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={backgroundStyle}
          >
            {isSubmitting ? (
              <span>Sending...</span>
            ) : (
              <>
                <Send size={16} />
                <span>Send Message</span>
              </>
            )}
          </button>

          {onContinueChat && (
            <button
              type="button"
              onClick={onContinueChat}
              className={`w-full py-2 mt-2 text-sm font-medium transition-all ${isDark ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'} underline`}
            >
              Continue chatting with AI
            </button>
          )}
        </form>

        <p className="text-xs text-gray-400 mt-3 text-center">
          Powered by <span className="font-semibold">G6th Sense</span>
        </p>
      </div>
    </div>
  );
};

export default OfflineForm;
